import { useEffect, useRef } from "react";

import { useReducedExperience } from "../lib/useExperienceLevel";

const PLANE_TRANSFORMS = {
  far: "translate3d(calc(var(--ambient-x, 0) * 8px), calc(var(--ambient-y, 0) * 6px + var(--ambient-scroll, 0) * -8px), 0)",
  mid: "translate3d(calc(var(--ambient-x, 0) * -16px), calc(var(--ambient-y, 0) * -11px + var(--ambient-scroll, 0) * 15px), 0)",
  near: "translate3d(calc(var(--ambient-x, 0) * 24px), calc(var(--ambient-y, 0) * 18px + var(--ambient-scroll, 0) * -22px), 0)",
} as const;

/**
 * Fondo compartido por landings, autenticación y estados públicos.
 *
 * Es deliberadamente sólido: el contenido nunca vuelve a quedar apoyado sobre auroras, manchas o
 * degradés de color. La profundidad se construye sólo con una grilla editorial, arcos y líneas de
 * registro en tres planos de parallax. Así sigue siendo un fondo vivo, pero no compite con títulos,
 * formularios ni portadas.
 */
export function AmbientBackground() {
  const rootRef = useRef<HTMLDivElement>(null);
  // Este fondo es `fixed` y vive en el layout, o sea en TODAS las landings. En
  // experiencia reducida no registra ni el listener de puntero ni el de scroll: queda un
  // fondo quieto, con la misma grilla, los mismos arcos y los mismos colores.
  // Las tres animaciones SVG infinitas las apaga el CSS por `.academia-register-motion`.
  const reduced = useReducedExperience();

  useEffect(() => {
    const root = rootRef.current;
    if (!root || reduced) return;

    let frame = 0;
    let targetX = 0;
    let targetY = 0;
    let pointerX = 0;
    let pointerY = 0;
    let scrollY = window.scrollY;
    let active = true;

    const paint = () => {
      pointerX += (targetX - pointerX) * 0.075;
      pointerY += (targetY - pointerY) * 0.075;
      root.style.setProperty("--ambient-x", pointerX.toFixed(3));
      root.style.setProperty("--ambient-y", pointerY.toFixed(3));
      root.style.setProperty("--ambient-scroll", Math.min(scrollY / 1200, 1).toFixed(3));

      const moving = Math.abs(targetX - pointerX) > 0.001 || Math.abs(targetY - pointerY) > 0.001;
      frame = moving && active ? requestAnimationFrame(paint) : 0;
    };
    const schedule = () => {
      if (!frame && active) frame = requestAnimationFrame(paint);
    };
    const onPointerMove = (event: PointerEvent) => {
      targetX = event.clientX / window.innerWidth - 0.5;
      targetY = event.clientY / window.innerHeight - 0.5;
      schedule();
    };
    const onPointerLeave = () => {
      targetX = 0;
      targetY = 0;
      schedule();
    };
    const onScroll = () => {
      scrollY = window.scrollY;
      schedule();
    };

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    document.documentElement.addEventListener("pointerleave", onPointerLeave, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    paint();

    return () => {
      active = false;
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onPointerMove);
      document.documentElement.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("scroll", onScroll);
      root.style.removeProperty("--ambient-x");
      root.style.removeProperty("--ambient-y");
      root.style.removeProperty("--ambient-scroll");
    };
  }, [reduced]);

  return (
    <div
      ref={rootRef}
      data-testid="ambient-background"
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0 isolate overflow-hidden bg-[#F7F3EA] dark:bg-[#191613]"
    >
      <style>{`
        @keyframes academia-register-drift {
          0%, 100% { transform: rotate(-1deg) translate3d(-.5%, 0, 0); }
          50% { transform: rotate(1deg) translate3d(.75%, -.5%, 0); }
        }
        @keyframes academia-register-turn {
          to { transform: rotate(360deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .academia-register-motion { animation: none !important; }
        }
      `}</style>

      {/* Papel cuadriculado muy tenue. SVG evita introducir cualquier degradé CSS. */}
      <div className="absolute inset-0 opacity-[0.055] dark:opacity-[0.07]">
        <svg className="size-full" aria-hidden="true">
          <defs>
            <pattern
              id="academia-editorial-grid"
              width="64"
              height="64"
              patternUnits="userSpaceOnUse"
            >
              <path d="M64 0H0V64" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#academia-editorial-grid)" />
        </svg>
      </div>

      {/* Plano lejano: una arquitectura de página, no una nube de color. */}
      <div className="absolute inset-[-12%]" style={{ transform: PLANE_TRANSFORMS.far }}>
        <svg
          viewBox="0 0 1440 900"
          preserveAspectRatio="xMidYMid slice"
          className="academia-register-motion size-full text-landing-text opacity-[0.08] [animation:academia-register-drift_34s_ease-in-out_infinite]"
        >
          <g fill="none" stroke="currentColor">
            <path d="M-80 178H1520M-80 712H1520" />
            <path d="M278-80V980M1162-80V980" />
            <circle cx="1162" cy="178" r="292" />
            <circle cx="278" cy="712" r="214" />
          </g>
        </svg>
      </div>

      {/* Plano medio: dos sistemas orbitales grandes y abiertos. */}
      <div className="absolute inset-[-10%]" style={{ transform: PLANE_TRANSFORMS.mid }}>
        <svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" className="size-full">
          <g fill="none" strokeLinecap="round">
            <g
              className="academia-register-motion origin-[1160px_170px] [animation:academia-register-turn_90s_linear_infinite]"
              stroke="#C0392B"
            >
              <path d="M862 170a298 298 0 0 1 596 0" strokeWidth="1.5" opacity=".16" />
              <path
                d="M922 170a238 238 0 0 1 476 0"
                strokeWidth="2"
                strokeDasharray="96 24 4 34"
                opacity=".22"
              />
            </g>
            <g
              className="academia-register-motion origin-[238px_742px] [animation:academia-register-turn_110s_linear_infinite_reverse]"
              stroke="#2B7D87"
            >
              <path d="M-50 742a288 288 0 0 0 576 0" strokeWidth="1.5" opacity=".15" />
              <path
                d="M8 742a230 230 0 0 0 460 0"
                strokeWidth="2"
                strokeDasharray="4 18 72 28"
                opacity=".21"
              />
            </g>
          </g>
        </svg>
      </div>

      {/* Plano cercano: una señal diagonal fina que reacciona al puntero. */}
      <div className="absolute inset-[-8%]" style={{ transform: PLANE_TRANSFORMS.near }}>
        <svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" className="size-full">
          <g fill="none">
            <path
              d="M-80 820C330 655 605 590 870 390C1080 232 1264 198 1520 98"
              stroke="#1F1A15"
              strokeWidth="1"
              strokeDasharray="2 18"
              opacity=".12"
              className="dark:stroke-[#F5F0E4]"
            />
            <path
              d="M-80 846C332 681 622 611 888 411C1092 258 1270 222 1520 124"
              stroke="#C0392B"
              strokeWidth="2"
              strokeDasharray="78 22 5 30"
              opacity=".16"
            />
          </g>
        </svg>
      </div>
    </div>
  );
}
